/**
 * TBMuktBharat Theme Toggle Component
 * Animated sun/moon toggle for light/dark theme switching
 */

import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Sun, Moon, Monitor } from 'lucide-react'
import { useTheme } from '@/contexts/ThemeContext'

interface ThemeToggleProps {
  variant?: 'icon' | 'button' | 'dropdown'
  size?: 'sm' | 'md' | 'lg'
  showLabel?: boolean
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  variant = 'icon',
  size = 'md',
  showLabel = false
}) => {
  const { theme, toggleTheme, isDark } = useTheme()

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  }

  const buttonSizes = {
    sm: 'p-1.5',
    md: 'p-2',
    lg: 'p-2.5'
  }

  // Simple icon toggle
  if (variant === 'icon') {
    return (
      <button
        onClick={toggleTheme}
        className={`
          ${buttonSizes[size]}
          rounded-full
          bg-gray-100 dark:bg-gray-800
          hover:bg-gray-200 dark:hover:bg-gray-700
          transition-colors duration-200
          focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500
        `}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={theme}
            initial={{ y: -20, opacity: 0, rotate: -90 }}
            animate={{ y: 0, opacity: 1, rotate: 0 }}
            exit={{ y: 20, opacity: 0, rotate: 90 }}
            transition={{ duration: 0.2 }}
          >
            {isDark ? (
              <Moon className={`${sizeClasses[size]} text-blue-400`} />
            ) : (
              <Sun className={`${sizeClasses[size]} text-amber-500`} />
            )}
          </motion.div>
        </AnimatePresence>
      </button>
    )
  }

  // Button with label
  if (variant === 'button') {
    return (
      <button
        onClick={toggleTheme}
        className={`
          flex items-center gap-2 px-4 py-2 rounded-lg
          bg-gray-100 dark:bg-gray-800
          hover:bg-gray-200 dark:hover:bg-gray-700
          transition-all duration-200
          font-medium text-sm
          focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500
        `}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={theme}
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            exit={{ scale: 0, rotate: 180 }}
            transition={{ duration: 0.2 }}
          >
            {isDark ? (
              <Moon className="w-4 h-4 text-blue-400" />
            ) : (
              <Sun className="w-4 h-4 text-amber-500" />
            )}
          </motion.div>
        </AnimatePresence>
        {showLabel && (
          <span className="text-gray-700 dark:text-gray-200">
            {isDark ? 'Light' : 'Dark'}
          </span>
        )}
      </button>
    )
  }

  return null
}

// Theme toggle with system option
export const ThemeToggleWithSystem: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const { theme, setTheme } = useTheme()

  const options = [
    { value: 'light', label: 'Light', icon: Sun, description: 'Light mode' },
    { value: 'dark', label: 'Dark', icon: Moon, description: 'Dark mode' },
  ] as const

  return (
    <div className="flex flex-col gap-1 p-1">
      {options.map((option) => {
        const Icon = option.icon
        const isSelected = theme === option.value

        return (
          <button
            key={option.value}
            onClick={() => {
              setTheme(option.value as 'light' | 'dark')
              onClose?.()
            }}
            className={`
              flex items-center gap-3 px-3 py-2 rounded-lg
              transition-all duration-200
              ${isSelected
                ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
              }
            `}
          >
            <Icon className="w-4 h-4" />
            <div className="flex flex-col items-start">
              <span className="font-medium text-sm">{option.label}</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {option.description}
              </span>
            </div>
            {isSelected && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="ml-auto w-2 h-2 rounded-full bg-blue-600 dark:bg-blue-400"
              />
            )}
          </button>
        )
      })}
    </div>
  )
}

export default ThemeToggle